import React, { Fragment } from 'react';
import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import kebabCase from 'lodash.kebabcase';
import cloneDeep from 'lodash.clonedeep';
import {
  PageHeader,
  TaxFilter,
  Section,
  SimpleDataTable,
  TaxAppendixTOC,
  IntroSectionSummary,
  DangerousWrapper
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';


const TaxType = (props) => {
  const { data, pageContext, location } = props;
  const {
    introduction,
    content,
    site,
    filter,
    toc
  } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent.parent;
  const { baseSectionURL } = pageContext;
  // Define props for the layout component.
  const layoutProps = {
    activeIndex: 0,
    pre: () => <TaxFilter {...{ location, filter }} />,
    above: () => <PageHeader title={content.title} category={content.budgetSection} />,
    // Create the breadcrumb for tax type pages.
    pageTitle: content.title,
    breadcrumbprops: [
      { href: baseSectionURL, text: 'Tax Expenditure Budget' }
    ],
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };

  // Pull the nested tax expenditure data to the same level as headers.
  const getInnerData = (table) => {
    const procTable = cloneDeep(table);
    const indata = procTable.data[0].taxExpenditures.map((row) => {
      const record = {
        taxExpenditure: (
          <React.Fragment>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link to={`${baseSectionURL}${kebabCase(content.title)}/${kebabCase(row.summaryTable.data[0].taxExpenditureName)}`}>{row.summaryTable.data[0].taxExpenditureNumber}</Link>
            <DangerousWrapper rawHtml={`<br>${row.summaryTable.data[0].taxExpenditureName}`} htmlTag="span" />
          </React.Fragment>
        ),
        ...row.summaryTable.data[0]
      };
      return({
        ...record
      });
    });
    procTable.data = indata;
    return procTable;
  };

  const summaryTable = getInnerData(content.summaryTable);

  // Get the content of the first p tag to use as tax intro summary
  const introSummary = introduction.data.find((item) => item.name === 'p').content;
  // metadata for the page
  const metaTitle = content.title;
  const metaDescription = `Tax expenditure data for tax type ${content.title}.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        {introduction && <IntroSectionSummary introText={introSummary} link={introduction.fields.slug} charLimit={500} />}
        <Section>
          <h3>{summaryTable.title}</h3>
          <SimpleDataTable title={`${content.title} ${content.budgetSection} ${summaryTable.title}`} table={summaryTable} maxColWidth={150} mantissa={1} download={location.pathname} />
        </Section>
        <TaxAppendixTOC data={toc} />
      </Layout>
    </Fragment>
  );
};

TaxType.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default TaxType;

export const query = graphql`
  query($slug: String!, $introslug: String!) {
    site {
      ...SiteMetadata
    }
    # Tax introduction data are generated as GenericJson by gatsby-plugin-create-generic-pages
    # Each taxType node has a introslug field that is for linking its introduction to itself
    # Look in plugins/gatsby-plugin-create-tax-type-pages to see how $introslug is formed and passed
    introduction: genericJson(fields: { slug: { eq: $introslug } }) {
      title
      fields {
        slug
      }
      data {
        content
        name
      }
    }
    content: taxType( fields: { slug: { eq: $slug } } ) {
      title
      budgetSection
      summaryTable {
        title
        headers {
          name
          type
          align
          value
        }
        total {
          taxExpenditure
          fyThisYear
          fyTwoYearsAgo
          fyOneYearsAgo
          fyThreeYearsAgo
          fyFourYearsAgo
        }
        data {
          taxExpenditures {
              summaryTable {
                data {
                  taxExpenditureName
                  taxExpenditureNumber
                  fyFourYearsAgo
                  fyThreeYearsAgo
                  fyTwoYearsAgo
                  fyOneYearsAgo
                  fyThisYear
                  footnote
                }
              }
          }
        }
      }
      # Get the last modified timestamp for the file powering the page
      ...TaxTypeDataTimeStamp
    }
    filter: taxItemJson {
      data {
        taxType
        taxExpenditure
        taxItemNumber
        taxItemName
      }
    }
    # Tax appenices data are generated as GenericJson by gatsby-plugin-create-generic-pages
    # For the TOC, get all generic data that has category field start with Appendix, e.g. Appendix A, Appendix B and Appendix D and sort them ascendantly
    # Look in src/data/docs/generics/meta.json for the category fields
    toc: allGenericJson(filter: {fields: {category: {regex: "/Appendix.*/"}}}, sort: {fields: fields___category, order: ASC}) {
      edges {
        node {
          fields {
            slug
            category
            title
          }
        }
      }
    }
  }
`;
